import { useAsyncFn } from 'react-use';

import { logger } from '@service-initiation/shared-clients-osp-logger';
import { stringifyAddress } from '@service-initiation/shared-data-access-google';
import { doRetry } from '@service-initiation/shared-utils-helpers';
import {
  ErrorType,
  getStores,
} from '@service-initiation/tech-care-data-access-tech-care-services';

import { storeIdAtom } from '../atoms';
import { store } from '../store';
import { type MailIn } from '../types';

const fetchClosestStore = async (mailIn: MailIn) => {
  if (mailIn?.shippingAddress) {
    const fullAddress = stringifyAddress(mailIn.shippingAddress);

    try {
      let startMaxDistance = 175;
      const closestStore =
        (await doRetry(
          async () => {
            const stores = await getStores({
              calcNextAvailability: false,
              filterByStoreAttributeAndValue: {
                attribute: 'mail_in_enabled',
                value: true,
              },
              fullAddress: fullAddress,
              maxDistance: startMaxDistance,
              maxNumOfStoresToReturn: 20,
            });
            startMaxDistance += 300;
            return stores;
          },
          async (currentRetryIteration, output) => {
            if (currentRetryIteration >= 5 || (await output)[0]) {
              return 'stop';
            }
            return 'again';
          }
        )) || [];

      if (closestStore.length <= 0) {
        throw new Error('No stores found for provided address');
      }
      return closestStore[0];
    } catch (e) {
      const err = e as Error;
      err.message =
        'Error occurred while trying to get relevant stores for provided address';
      logger.error(err);
      throw err;
    }
  }
};

export const useFindClosestStoreForMailIn = () =>
  useAsyncFn(async (mailIn: MailIn) => {
    const closestStore = await fetchClosestStore(mailIn);
    if (closestStore) {
      store.set(storeIdAtom, closestStore.id);
      return closestStore;
    } else {
      throw new Error(ErrorType.Error);
    }
  }, []);
